/* istanbul ignore file */

import type { MutableRefObject } from 'react';
import { useEffect, useRef, useState } from 'react';
import { useScroll } from 'react-use';
import { isBreakpointLargeOrAbove, useBreakpoint } from '@noths/polaris-client-ribbons-base';

import type { NavigatePayload } from '../types';
import {
  getNextPosition,
  getPreviousPosition,
  hasNavigation,
  hasNextItem,
  hasPreviousItem,
  isNextItem,
  isPreviousItem,
} from '../utils';
import { useCarouselIntersections } from './useCarouselIntersections';

export const useCarousel = (
  wrapper: MutableRefObject<HTMLElement | null>,
  elements: MutableRefObject<(HTMLElement | null)[]>,
  onNavigate?: (payload: NavigatePayload) => void,
) => {
  const { x, y } = useScroll(wrapper);
  const [position, setPosition] = useState(0);
  const [hasNext, setNext] = useState(false);
  const [hasPrevious, setPrevious] = useState(false);
  const intersections = useCarouselIntersections(wrapper, elements);
  const breakpoint = useBreakpoint({ initializeWithLarge: true });
  const isLargeScreen = isBreakpointLargeOrAbove(breakpoint);

  const onNext = () => {
    const nextPosition =
      !!wrapper.current && getNextPosition(position, wrapper.current, intersections);
    if (nextPosition !== false) {
      setPosition(nextPosition);
    }

    onNavigate?.({ navigation: { type: 'Arrow Click', direction: 'Right' } });
  };

  const onPrevious = () => {
    const previousPosition =
      !!wrapper.current && getPreviousPosition(position, wrapper.current, intersections);
    if (previousPosition !== false) {
      setPosition(previousPosition);
    }

    onNavigate?.({ navigation: { type: 'Arrow Click', direction: 'Left' } });
  };

  const onFocus = (element: HTMLElement) => {
    if (isNextItem(intersections, element)) {
      onNext();
    }
    if (isPreviousItem(intersections, element)) {
      onPrevious();
    }
  };

  useEffect(() => {
    setNext(hasNextItem(intersections));
    setPrevious(hasPreviousItem(intersections));
  }, [hasNextItem, hasPreviousItem, intersections]);

  // reset scroll position when moving between breakpoints
  useEffect(() => {
    if (isLargeScreen && wrapper.current) {
      wrapper.current.scrollLeft = 0;
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isLargeScreen, wrapper.current && wrapper.current.scrollLeft]);

  const touchXposition = useRef(0);

  // Calculate touch drag direction
  useEffect(() => {
    const carouselWrapper = wrapper.current;
    const touchStartListener = (e: TouchEvent) => {
      touchXposition.current = e.changedTouches[0].clientX;
    };
    const touchEndListener = (e: TouchEvent) => {
      const direction = e.changedTouches[0].clientX > touchXposition.current ? 'Left' : 'Right';
      onNavigate?.({ navigation: { type: 'Scroll', direction } });
    };
    const listenerOptions: AddEventListenerOptions & EventListenerOptions = { passive: true };

    carouselWrapper?.addEventListener('touchstart', touchStartListener, listenerOptions);
    carouselWrapper?.addEventListener('touchend', touchEndListener, listenerOptions);

    return () => {
      carouselWrapper?.removeEventListener('touchstart', touchStartListener, listenerOptions);
      carouselWrapper?.removeEventListener('touchend', touchEndListener, listenerOptions);
    };
  }, [onNavigate, wrapper]);

  return {
    position: isLargeScreen ? position : 0,
    onNext,
    onPrevious,
    onFocus,
    hasNext,
    hasPrevious,
    hasNavigation: hasNavigation(intersections),
    xPosition: x,
    yPosition: y,
  };
};
